import React, { use, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useLazyQuery } from '@apollo/client/react';
import cn from 'clsx';
import styles from './styles.module.scss';
import { BasicDropDownController } from '@components-clean/core/basic-dropdown';
import { DatePickerController } from '@components-clean/core/date-picker';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { TextAreaController } from '@components-clean/core/text-area';
import { TextAreaDropdownController } from '@components-clean/core/text-area-dropdown';
import { formatDatasetDates, sortDatasetByDatesDesc } from '@components-clean/navbar/dataset-selector.utils';
import { useUnsavedChangesTracking, useUnsavedChangesConfirmation } from '@hooks-clean/index';
import { CM2Dataset } from '@interfaces/central-monitoring';
import {
    GroupedByMultiSelectOption,
    GroupedByMultiSelectOptionStringId,
    MultiSelectOption,
} from '@interfaces-clean/dropdowns';
import { SubjectAssociationType } from '@interfaces-clean/subject-monitoring/subject-observations';
import style from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/style.module.scss';
import { sortStudyMemberData } from '@pages-clean/subject-monitoring/components/dialogs/helpers';
import { SMActiveReviewContext } from '@pages-clean/subject-monitoring/components/monitoring-toolkit/active-review-tab/context';
import { assumeUtc } from '@utils-clean/date-time-helpers';
import { useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromLocal } from '@utils/convertor';
import { ShowError } from '@utils/notification';
import {
    dataListingsConfigTableForStudy,
    dataListingsConfigTableForStudyVariables,
} from '@queries/generated/subject-monitoring.generated';
import {
    GET_SUBJECT_MONITORING_CONFIGURATION_FOR_TIMESTAMP,
    GET_SUBJECT_PROFILE_DETAILS_CONFIGURATION,
} from '@queries/subject-monitoring';
import DatasetApi from '@apis/bff-api/central-monitoring/dataset';
import { SubjectMonitoringStudyMemberApi } from '@apis/bff-api/subject-monitoring/subject-monitoring-study-member-api';
import SubjectObservationsApi from '@apis/bff-api/subject-monitoring/subject-observations';
import { SubjectMonitoringAPI } from '@apis/bff-api/subject-monitoring/subject-profile';
import { SubjectMonitoringReviewAPI } from '@apis/bff-api/subject-monitoring-review';

interface AddSmObservationDialogProps {
    onClose: () => void;
    onSubmit: (id: number) => void;
    orgKey: string;
    studyRef: string;
}

interface AddObservationForm {
    datasetOpenedOn: CM2Dataset | null;
    association: MultiSelectOption | null;
    subjectId: MultiSelectOption | null;
    data: GroupedByMultiSelectOptionStringId | null;
    review: GroupedByMultiSelectOption | null;
    observationDate: Date | null;
    owner: GroupedByMultiSelectOptionStringId | null;
    observation: string;
    rootCause: string | null;
    comments: string | null;
    closedDate: Date | null;
}

const defaultValues = {
    datasetOpenedOn: null,
    association: null,
    subjectId: null,
    data: null,
    review: null,
    observationDate: null,
    owner: null,
    observation: '',
    rootCause: '',
    comments: '',
    closedDate: null,
};

const COMPONENT_NAME = 'add-sm-observation-dialog';

const AddSmObservationDialog = ({ orgKey, studyRef, onClose, onSubmit }: AddSmObservationDialogProps) => {
    // -----------
    // States
    // -----------
    const [configuredDataListings, setConfiguredDataListings] = useState<string[]>([]);
    const [configuredDataPanelIds, setConfiguredDataPanelIds] = useState<string[]>([]);

    // -----------
    // API calls
    // -----------
    const { data: activeDatasets, isLoading: isLoadingDatasets } = DatasetApi.useGetActive(studyRef);
    const sortedCM2DataSets = useMemo(
        () =>
            (activeDatasets?.filter((x) => x.active === true)?.filter(Boolean) ?? [])
                .map(formatDatasetDates)
                .sort(sortDatasetByDatesDesc),
        [activeDatasets],
    );
    const getAllReviewsQuery = SubjectMonitoringReviewAPI.useGetAllReviewsQuery(studyRef);
    const { data: smStudyMembersData, isLoading: isLoadingStudyMembers } =
        SubjectMonitoringStudyMemberApi.useGetAllStudyMembers(studyRef, true);

    const smActiveReviewContext = use(SMActiveReviewContext);
    const sessionId = smActiveReviewContext?.state?.sessionId;

    const { trigger: saveObservation } = SubjectObservationsApi.useSave(studyRef, sessionId);
    const { t } = useTranslation('common');

    // -----------
    // Queries
    // -----------
    const [loadSmConfig, { data: smConfigData, loading: isLoadingSmConfig }] = useLazyQuery(
        GET_SUBJECT_PROFILE_DETAILS_CONFIGURATION,
    );
    const [loadSubjectMonitoringConfig, { data: subjectConfigData, loading: isLoadingSubjectConfig }] = useLazyQuery<
        dataListingsConfigTableForStudy,
        dataListingsConfigTableForStudyVariables
    >(GET_SUBJECT_MONITORING_CONFIGURATION_FOR_TIMESTAMP);

    const getSmDataListingsQuery = SubjectMonitoringAPI.useGetSmDataListings(studyRef, {
        configuredDataTableIds: configuredDataListings,
    });

    const getSmPanelNamesQuery = SubjectMonitoringAPI.useGetSmPanelNames(studyRef, {
        configuredPanelIds: configuredDataPanelIds,
    });

    // -----------
    // Hooks
    // -----------
    const { t: mtkTranslation }: { t: (key: string) => string } = useTranslation('sm', {
        keyPrefix: 'monitoringToolkit',
    });

    const owners: GroupedByMultiSelectOptionStringId[] = useMemo(() => {
        return sortStudyMemberData(smStudyMembersData);
    }, [smStudyMembersData]);

    const onCreateObs = async (formData: AddObservationForm) => {
        try {
            const {
                data,
                observation,
                observationDate,
                closedDate,
                review,
                owner,
                rootCause,
                comments,
                association,
                datasetOpenedOn,
                subjectId,
            } = formData;

            const datasetDate = datasetOpenedOn?.dataCutDate
                ? (stringDateWithoutTimeFromLocal(datasetOpenedOn?.dataCutDate) ?? undefined)
                : undefined;

            const associationType = association?.id! as SubjectAssociationType;

            let dataListingRef: string | undefined;
            let dataListingName: string | undefined;
            if (associationType === 1 && data && data.groupedBy === 'Data Listing') {
                dataListingRef = data.id;
                dataListingName = data.value;
            }

            let panelRef: string | undefined;
            let panelName: string | undefined;
            if (associationType === 1 && data && data.groupedBy === 'Panel') {
                panelRef = data.id;
                panelName = data.value;
            }

            const response = await saveObservation({
                datasetId: datasetOpenedOn?.id,
                associationType,
                dataListingRef,
                dataListingName,
                panelRef,
                panelName,
                observationDate: assumeUtc(observationDate),
                ownerRef: owner?.id,
                ownerName: owner?.value,
                observation,
                rootCause,
                comments,
                closedDate: assumeUtc(closedDate),
                dataSetDate: datasetDate,
                reviewId: review?.id,
                reviewTitle: review?.value,
                subjectId: subjectId?.id!,
            });

            if (response?.success) {
                return response.id!;
            }
            return 0;
        } catch {
            ShowError(t('errorMessage'));
            return 0;
        }
    };

    const submitAndClose = async (formData: AddObservationForm) => {
        const id = await onCreateObs(formData);
        onSubmit(id);
    };

    const {
        control,
        handleSubmit,
        formState: { isValid, isDirty, isSubmitting },
        resetField,
        reset,
    } = useForm<AddObservationForm>({ mode: 'all', defaultValues });

    const watchedDatasetOpenedOn = useWatch({ control, name: 'datasetOpenedOn' });
    const watchedAssociation = useWatch({ control, name: 'association' });

    useUnsavedChangesTracking({
        area: 'subject-monitoring-toolkit',
        componentName: COMPONENT_NAME,
        hasChanges: isDirty,
    });
    const confirmLossOfChanges = useUnsavedChangesConfirmation({
        area: 'subject-monitoring-toolkit',
        mode: 'individual-components',
        componentNames: COMPONENT_NAME,
    });

    const dataDropdown = useMemo<GroupedByMultiSelectOptionStringId[]>(() => {
        const smPanelNames = (getSmPanelNamesQuery.data ?? [])
            .map(
                (panel) =>
                    ({
                        id: panel.id!,
                        value: panel.name!,
                        active: true,
                        groupedBy: 'Panel',
                    }) satisfies GroupedByMultiSelectOptionStringId,
            )
            .toSorted((a, b) => {
                const aVal = a.value ?? ''; // null → ""
                const bVal = b.value ?? '';
                return aVal.localeCompare(bVal);
            });

        const smDataListings = (getSmDataListingsQuery.data ?? [])
            .map(
                (dataListing) =>
                    ({
                        id: dataListing.id!,
                        value: dataListing.name!,
                        active: true,
                        groupedBy: 'Data Listing',
                    }) satisfies GroupedByMultiSelectOptionStringId,
            )
            .toSorted((a, b) => {
                const aVal = a.value ?? ''; // null → ""
                const bVal = b.value ?? '';
                return aVal.localeCompare(bVal);
            });

        return [...smPanelNames, ...smDataListings];
    }, [getSmPanelNamesQuery.data, getSmDataListingsQuery.data]);

    useEffect(() => {
        if (!subjectConfigData || !smConfigData) {
            return;
        }
        const configuredDataListingDataTableIds =
            subjectConfigData?.dataListingsConfigTableForStudy?.dataListingsItems?.map(
                (x) => x?.datatableId as string,
            ) ?? [];
        const panelIds =
            smConfigData.subjectProfileDetailsConfiguration?.subjectProfileDetailsConfigurationItem?.timeline?.panels?.map(
                (panel) => panel.id,
            ) ?? [];

        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        /* eslint-disable @eslint-react/set-state-in-effect */
        setConfiguredDataListings(configuredDataListingDataTableIds);
        setConfiguredDataPanelIds(panelIds);
        /* eslint-enable @eslint-react/set-state-in-effect */
    }, [subjectConfigData, smConfigData]);

    const reviewRecordIdToIdMap = useMemo(() => {
        return new Map(getAllReviewsQuery?.data?.map((review) => [review.recordId, review.id]));
    }, [getAllReviewsQuery.data]);

    const mappedReviews: GroupedByMultiSelectOption[] = useMemo(() => {
        return (
            getAllReviewsQuery?.data
                ?.map((x) => {
                    return {
                        id: x.id ?? 0,
                        value: x.title ?? '',
                        active: true,
                        groupedBy: x.reviewed ? 'Closed' : 'Open',
                    };
                })
                .sort((a, b) => {
                    // Normalize nullable boolean (true → 1, false/null → 0)
                    const aActive = a.groupedBy === 'Open' ? 1 : 0;
                    const bActive = b.groupedBy === 'Open' ? 1 : 0;

                    // Primary sort: active descending (true first)
                    if (bActive !== aActive) {
                        return bActive - aActive;
                    }

                    // Normalize nullable strings
                    const aTitle = a.value ?? '';
                    const bTitle = b.value ?? '';

                    // Secondary sort: alphabetical ascending
                    return aTitle.localeCompare(bTitle);
                }) ?? []
        );
    }, [getAllReviewsQuery.data]);

    useEffect(() => {
        resetField('data');

        if (!watchedAssociation || !watchedDatasetOpenedOn?.created) {
            return;
        }

        if (watchedAssociation.id === 1) {
            loadSmConfig({ variables: { studyId: studyRef, timestamp: watchedDatasetOpenedOn.created! } }).catch(
                (err) => window.console.error(err),
            );
            loadSubjectMonitoringConfig({
                variables: { studyId: studyRef, timestamp: watchedDatasetOpenedOn.created! },
            }).catch((err) => window.console.error(err));
        }
    }, [
        loadSmConfig,
        orgKey,
        resetField,
        studyRef,
        watchedAssociation,
        watchedDatasetOpenedOn?.created,
        loadSubjectMonitoringConfig,
    ]);

    const { data: subjectsData, isLoading: isLoadingSubjects } = SubjectMonitoringAPI.useGetStudySubjects(studyRef);

    useEffect(() => {
        if (!smActiveReviewContext?.state?.reviewRecordId || !mappedReviews || !reviewRecordIdToIdMap || !reset) {
            return;
        }

        // If we're in review mode, and that review is in the dropdown, set it to that review by default
        const reviewId = reviewRecordIdToIdMap.get(smActiveReviewContext?.state?.reviewRecordId);
        if (!reviewId) {
            return;
        }
        const review = mappedReviews.find((r) => r.id === reviewId);
        if (review) {
            reset({ ...defaultValues, review });
        }
    }, [smActiveReviewContext?.state?.reviewRecordId, mappedReviews, reviewRecordIdToIdMap, reset]);

    const subjects = useMemo(
        () =>
            subjectsData
                ?.filter((subject) => !!subject)
                .map((subject) => {
                    return {
                        id: subject.id,
                        value: subject.subjectNumber,
                    };
                })
                .toSorted((a, b) =>
                    a.value.localeCompare(b.value, undefined, {
                        numeric: true,
                    }),
                ),
        [subjectsData],
    );

    const onCloseHandler = async () => {
        const confirmed = await confirmLossOfChanges();
        if (!confirmed) {
            return;
        }

        onClose();
    };

    return (
        <DraggableDialog
            dataTestId="add-sm-observation-dialog"
            title={mtkTranslation('addObservationDialog.title')}
            onClose={onCloseHandler}
            onSubmit={() => handleSubmit(submitAndClose)()}
            primaryBtnDisabled={!isValid || !isDirty || isSubmitting}
            secondaryBtnDisabled={isSubmitting}
            initialHeight={630}
            initialWidth={522}
        >
            <form id="add-edit-observation" className="k-w-full k-h-full">
                <div className={cn('k-shrink-0 k-d-flex k-justify-content-center k-pb-md', styles.formFieldGap)}>
                    <div className="!k-basis-1/2">
                        <BasicDropDownController
                            control={control}
                            name="datasetOpenedOn"
                            data-testid="field-select-datasetOpenedOn"
                            data={sortedCM2DataSets}
                            required
                            textField="dataCutDate"
                            dataItemKey="id"
                            label={`${mtkTranslation('addObservationDialog.datasetOpenedOn')} *`}
                            loading={isLoadingDatasets}
                            disabled={isLoadingDatasets}
                            filterable
                            allowClear
                            readOnly={isSubmitting}
                            onChange={() => {
                                resetField('subjectId');
                                resetField('association');
                                resetField('data');
                            }}
                        />
                    </div>
                    <div className="!k-basis-1/2">
                        <BasicDropDownController
                            control={control}
                            name="association"
                            data-testid="field-select-association"
                            label={`${mtkTranslation('addObservationDialog.association')} *`}
                            data={[
                                { id: 0, value: 'Subject Hub' },
                                { id: 1, value: 'Subject Profile' },
                            ]}
                            readOnly={isSubmitting}
                            filterable
                            allowClear
                            dataItemKey="id"
                            textField="value"
                            disabled={!watchedDatasetOpenedOn}
                        />
                    </div>
                </div>
                <div className={cn('k-shrink-0 k-d-flex k-justify-content-center k-pb-md', styles.formFieldGap)}>
                    <div className="!k-basis-1/2">
                        <BasicDropDownController
                            control={control}
                            name="data"
                            data-testid="field-select-data"
                            label={`${mtkTranslation('addObservationDialog.data')}`}
                            data={dataDropdown}
                            readOnly={
                                isSubmitting ||
                                getSmPanelNamesQuery.isLoading ||
                                getSmDataListingsQuery.isLoading ||
                                isLoadingSmConfig ||
                                isLoadingSubjectConfig
                            }
                            filterable
                            allowClear
                            dataItemKey="id"
                            textField="value"
                            groupField="groupedBy"
                            disabled={!watchedDatasetOpenedOn || !watchedAssociation || watchedAssociation.id === 0}
                        />
                    </div>
                    <div className="!k-basis-1/2">
                        <BasicDropDownController
                            control={control}
                            name="subjectId"
                            data-testid="field-select-subject-code"
                            data={subjects ?? []}
                            textField="value"
                            disabled={!watchedDatasetOpenedOn || isSubmitting || isLoadingSubjects}
                            label={`${mtkTranslation('addObservationDialog.subject')} *`}
                            required
                            loading={isLoadingSubjects}
                            dataItemKey="id"
                            filterable
                            allowClear
                            readOnly={isSubmitting}
                        />
                    </div>
                </div>
                <div className={cn('k-shrink-0 k-d-flex k-justify-content-center k-pb-md', styles.formFieldGap)}>
                    <div className="!k-basis-1/2">
                        <BasicDropDownController
                            control={control}
                            name="review"
                            data={mappedReviews}
                            data-testid="field-review"
                            dataItemKey="id"
                            textField="value"
                            fillMode="solid"
                            filterable
                            groupField="groupedBy"
                            virtualPageSize={100}
                            allowClear
                            readOnly={isSubmitting || getAllReviewsQuery.isLoading}
                            label={`${mtkTranslation('addObservationDialog.reviewAssociation')}`}
                        />
                    </div>
                    <div className="!k-basis-1/2">
                        <DatePickerController
                            control={control}
                            name="observationDate"
                            data-testid="datepicker-observationDate"
                            label={`${mtkTranslation('addObservationDialog.observationDate')} *`}
                            fillMode="solid"
                            required
                            readonly={isSubmitting}
                        />
                    </div>
                </div>
                <div className={cn('k-shrink-0 k-d-flex k-justify-content-center k-pb-md', styles.formFieldGap)}>
                    <BasicDropDownController
                        control={control}
                        name="owner"
                        data-testid="new-review-lovs"
                        data={owners}
                        textField="value"
                        dataItemKey="id"
                        label={`${mtkTranslation('addObservationDialog.owner')}`}
                        readOnly={isSubmitting || isLoadingStudyMembers}
                        groupField="groupedBy"
                        allowClear
                        filterable
                    />
                </div>
                <div className={cn('k-shrink-0 k-d-flex k-justify-content-center k-pb-md', styles.formFieldGap)}>
                    <TextAreaController
                        className={style.textAreaOverrides}
                        maxLength={3000}
                        control={control}
                        name="observation"
                        required
                        data-testid="field-select-observation"
                        label={`${mtkTranslation('addObservationDialog.observation')} *`}
                        readOnly={isSubmitting}
                    />
                </div>
                <div className="k-pb-md k-input-md">
                    <TextAreaDropdownController
                        maxLength={3000}
                        className={style.textAreaOverrides}
                        control={control}
                        name="rootCause"
                        data-testid="field-select-rootCause"
                        label={mtkTranslation('addObservationDialog.rootCause')}
                        rows={4}
                        fillMode="outline"
                        readOnly={isSubmitting}
                    />
                </div>
                <div className="k-pb-md k-input-md">
                    <TextAreaDropdownController
                        control={control}
                        className={style.textAreaOverrides}
                        name="comments"
                        data-testid="field-select-comments"
                        label={mtkTranslation('addObservationDialog.comments')}
                        rows={4}
                        fillMode="outline"
                        maxLength={3000}
                        readOnly={isSubmitting}
                    />
                </div>
                <div className="k-shrink-0 k-d-flex k-justify-content-start k-pb-md">
                    <div className="k-flex-row !k-basis-1/2">
                        <DatePickerController
                            control={control}
                            name="closedDate"
                            data-testid="datepicker-closedDate"
                            label={mtkTranslation('addObservationDialog.closedDate')}
                            fillMode="solid"
                            readonly={isSubmitting}
                        />
                    </div>
                </div>
            </form>
        </DraggableDialog>
    );
};

export default AddSmObservationDialog;
